// entityRegistry — the tab + action registries for an entity surface
// (ADR-036 §7). Mirrors the nav-config registry (config/nav.ts): a declarative
// list of entries with the SAME gate shape ({id, labelKey, route|handler, gate,
// entitlement, order}) + pure, unit-tested derivations. Actions add a `show(ctx)`
// visibility predicate over the FULL action context — status (Suspend while
// active, Restore while suspended) AND the consumer's closed-over relational facts
// (e.g. users' isSelf) — and a KIND.
//
// THE FOUR KINDS (ADR-036 §7 — the two-control law): every act an entity offers
// is ONE row of its registry and declares what it is —
//   • primary   — at most one visible: the act people most often come for (Add
//                 members on a team). The page's SEAT, the peek's quiet main button.
//                 One act with several OUTCOMES (Decide — accept · an exception ·
//                 problematic · exclude) holds them: the peek's main button and the
//                 seat open them as a menu, the danger outcome fenced last.
//   • edit      — one per section, opening that section's drawer; the peek's Edit
//                 menu, the page's section link (`section` names the card). A row
//                 with `choices` opens a one-decision chooser first (ADR-038 §1).
//   • operation — reset a password, send an invite, clone, set as default, copy
//                 a link. The door.
//   • lifecycle — disable · reactivate · suspend · resume · activate · replace ·
//                 restore; its DANGER act (remove · delete · decommission ·
//                 terminate) is a lifecycle row with `tone: 'danger'`, fenced last.
// The surfaces compose from the SAME resolved set (`EntityAct[]`): `ContextPanel`
// draws the peek's top row + its footer door, `EntityHeader` the page's door +
// seat, `SectionAct` a card's own act — one registry per entity, every surface
// read from it, so they cannot drift (dev's failure mode was per-page reinvented
// menus). The per-entity module (`pages/<entity>/<Entity>Actions.tsx`) owns the
// rows, the gating and the overlays; the exemplar is the team.
//
// i18n- and router-agnostic by design (like config/nav.ts): the derivations
// filter + order; the CONSUMER resolves labelKey → label (t()) through
// `resolveActs`. That keeps the registry pure — testable, no DOM, no i18n.

import { createElement, type ReactNode } from 'react';
import type { EntitlementFlag } from '@ems-app/schemas/permissions';
import type { NavGate } from '../../config/nav.js';
import type { Icon } from '../ui/icons.js';

/** The gate vocabulary is shared with the nav registry (a catalogue
 *  `PermissionKey` or a synthetic atom — `config/nav.ts`) — `useCan(projectId)`
 *  returns exactly this predicate, so it plugs into the derivations unchanged. */
export type EntityGate = NavGate;

/** What an action does when chosen — navigate (a real route, so modifier-click
 *  opens a new tab) or run a handler (open a form container / fire a mutation).
 *  Exactly one. */
export type ActionRun = { readonly route: string } | { readonly handler: () => void };

/** Fire a run from a button: a route navigates (the surface's `navigate`), a handler runs. */
export function runAct(run: ActionRun, navigate: (to: string) => void): void {
  if ('route' in run) navigate(run.route);
  else run.handler();
}

/** The four kinds — ADR-036 §7 (the header comment). */
export type ActionKind = 'primary' | 'edit' | 'operation' | 'lifecycle';

/** One verb of a chooser (an edit row with several verbs — "the setup changed" ·
 *  "correct the record"): its own words, help line and run. */
export interface ActionChoice {
  readonly id: string;
  readonly labelKey: string;
  readonly helpKey: string;
  readonly icon?: Icon;
  readonly run: ActionRun;
}

/** One outcome of a primary that holds several (Decide — "Accept" · "Validate as
 *  exception" · …): its own words and run; the danger outcome is fenced last. A
 *  disabled outcome says why (`disabledNoteKey` — "No match — decide another way"). */
export interface ActionOutcome {
  readonly id: string;
  readonly labelKey: string;
  readonly icon?: Icon;
  readonly run: ActionRun;
  readonly tone?: 'default' | 'danger';
  readonly disabled?: boolean;
  readonly disabledNoteKey?: string;
}

/** What a row does when chosen — ONE of: `run` (navigate or a handler);
 *  `choices` + `questionKey` (an edit's one-decision chooser opens first; the
 *  chosen verb's `run` follows); or `outcomes` (a primary's menu of outcomes). */
export type ActionDoor =
  | { readonly run: ActionRun; readonly choices?: undefined; readonly questionKey?: undefined; readonly outcomes?: undefined }
  | { readonly run?: undefined; readonly choices: readonly ActionChoice[]; readonly questionKey: string; readonly outcomes?: undefined }
  | { readonly run?: undefined; readonly choices?: undefined; readonly questionKey?: undefined; readonly outcomes: readonly ActionOutcome[] };

interface ActionItemBase<S extends string = string> {
  readonly id: string;
  /** i18n key (the entity namespace, or `common:…`) — resolved by the consumer, never here. */
  readonly labelKey: string;
  /** A REGISTRY reference (`icons.edit`), never a node — each surface draws it
   *  at its own size, and the seat's registration never thrashes on a fresh element. */
  readonly icon?: Icon;
  readonly kind: ActionKind;
  /** The `DetailSection` an EDIT row belongs to — `SectionAct` reads it — or the
   *  tab that seats it (`sectionActOf` + `seatOfAct`: the asset's Connectivity tab). An act
   *  has ONE home per surface: the primary sits in the seat (the page) or the
   *  main button (the peek) and never also as a card's link, so a primary names
   *  no section. */
  readonly section?: string;
  readonly gate: EntityGate;
  readonly entitlement?: EntitlementFlag;
  /** Order WITHIN the kind (the door orders by kind first). */
  readonly order: number;
  /** Destructive tone (Decommission, Delete) — tints the row; on a lifecycle row
   *  it is the DANGER act, fenced last in the door. */
  readonly tone?: 'default' | 'danger';
  /** Visibility predicate over the FULL action context (ADR-036 §7): show only
   *  when it returns true. Reads `ctx.status` for the lifecycle flip (Suspend
   *  while active, Restore while suspended) and any relational fact the consumer
   *  closes over (e.g. users' `isSelf`). Omit = always shown (still subject to
   *  gate + entitlement). */
  readonly show?: (ctx: ActionContext<S>) => boolean;
  /** Shown but not actionable — a verb advertised before it is wired (a designed
   *  state), or an act outside the viewer's reach (`disabledNoteKey` says why:
   *  the zones' "Outside your scope"). */
  readonly disabled?: boolean;
  readonly disabledNoteKey?: string;
}

/** One entity action — the nav-registry shape + a kind, a section, a tone and a
 *  lifecycle-status predicate. Generic over the entity's status enum `S` so the
 *  predicate is typed per entity. */
export type ActionItem<S extends string = string> = ActionItemBase<S> & ActionDoor;

/** One heavy-entity tab — the nav-registry shape, no lifecycle predicate (tabs
 *  don't gate on status). Resolves to EntityHeader's `EntityTab`. */
export interface TabItem {
  readonly id: string;
  readonly labelKey: string;
  /** Sub-route segment under the entity path (e.g. `'alarms'`). */
  readonly route: string;
  readonly gate: EntityGate;
  readonly entitlement?: EntitlementFlag;
  readonly order: number;
  /** `end` for the index/Overview tab (so it doesn't match every sub-route). */
  readonly end?: boolean;
}

/** Gate context — mirrors `NavContext` (config/nav.ts). */
export interface GateContext {
  readonly can: (gate: EntityGate) => boolean;
  readonly entitlements: ReadonlySet<EntitlementFlag>;
}

/** Action context — the gate context + the entity's current lifecycle status. */
export interface ActionContext<S extends string = string> extends GateContext {
  readonly status: S;
}

/** Permission AND entitlement — the shared half (mirrors `isNavItemVisible`). */
function passesGate(
  gate: EntityGate,
  entitlement: EntitlementFlag | undefined,
  ctx: GateContext,
): boolean {
  if (!ctx.can(gate)) return false;
  if (entitlement && !ctx.entitlements.has(entitlement)) return false;
  return true;
}

/** Visible actions, in `order` — filtered by gate AND entitlement AND the
 *  `show(ctx)` visibility predicate. Every door (peek · the Actions button ·
 *  later ⌘K / bulk bar) renders this, so they can't disagree. */
export function visibleActions<S extends string>(
  registry: readonly ActionItem<S>[],
  ctx: ActionContext<S>,
): ActionItem<S>[] {
  return registry
    .filter((a) => passesGate(a.gate, a.entitlement, ctx))
    .filter((a) => (a.show ? a.show(ctx) : true))
    .sort((a, b) => a.order - b.order);
}

/** Visible tabs, in `order` — filtered by gate AND entitlement. The consumer
 *  maps these to EntityHeader's `EntityTab` (label via t(), href via the entity
 *  path builder). */
export function visibleTabs(registry: readonly TabItem[], ctx: GateContext): TabItem[] {
  return registry.filter((t) => passesGate(t.gate, t.entitlement, ctx)).sort((a, b) => a.order - b.order);
}

// ── The resolved set — what the surfaces compose from ──────────────────────

/** A chooser verb, its words resolved. */
export interface ResolvedChoice {
  readonly id: string;
  readonly label: string;
  readonly help: string;
  readonly icon?: Icon;
  readonly run: ActionRun;
}

/** An outcome, its words resolved. */
export interface ResolvedOutcome {
  readonly id: string;
  readonly label: string;
  readonly icon?: Icon;
  readonly run: ActionRun;
  readonly tone?: 'default' | 'danger';
  readonly disabled?: boolean;
  readonly disabledNote?: string;
}

/** One act of an entity, visible and worded — the registry row after
 *  `visibleActions` and the consumer's t(). The shells (`ContextPanel` ·
 *  `EntityHeader`) and `SectionAct` take a list of these. */
export interface EntityAct {
  readonly id: string;
  readonly kind: ActionKind;
  readonly label: string;
  readonly icon?: Icon;
  readonly section?: string;
  readonly run?: ActionRun;
  readonly choices?: readonly ResolvedChoice[];
  /** The chooser's question ("What happened?") — set with `choices`. */
  readonly question?: string;
  /** A primary's outcomes — its menu. */
  readonly outcomes?: readonly ResolvedOutcome[];
  readonly tone?: 'default' | 'danger';
  readonly disabled?: boolean;
  readonly disabledNote?: string;
}

/** The registry → the resolved set: visibility applied, the words resolved
 *  through the consumer's `t` (the entity namespace; `common:…` keys pass through). */
export function resolveActs<S extends string>(
  registry: readonly ActionItem<S>[],
  ctx: ActionContext<S>,
  t: (key: string) => string,
): EntityAct[] {
  return visibleActions(registry, ctx).map((a) => ({
    id: a.id,
    kind: a.kind,
    label: t(a.labelKey),
    icon: a.icon,
    section: a.section,
    run: a.run,
    choices: a.choices?.map((c) => ({ id: c.id, label: t(c.labelKey), help: t(c.helpKey), icon: c.icon, run: c.run })),
    question: a.questionKey ? t(a.questionKey) : undefined,
    outcomes: a.outcomes?.map((o) => ({ id: o.id, label: t(o.labelKey), icon: o.icon, run: o.run, tone: o.tone, disabled: o.disabled, disabledNote: o.disabledNoteKey ? t(o.disabledNoteKey) : undefined })),
    tone: a.tone,
    disabled: a.disabled,
    disabledNote: a.disabledNoteKey ? t(a.disabledNoteKey) : undefined,
  }));
}

/** The one primary act, if the viewer has it (at most one is visible — the shape test holds it). */
export function primaryOf(acts: readonly EntityAct[]): EntityAct | undefined {
  return acts.find((a) => a.kind === 'primary');
}

/** The edit rows — the peek's Edit menu. */
export function editsOf(acts: readonly EntityAct[]): EntityAct[] {
  return acts.filter((a) => a.kind === 'edit');
}

/** A card's own act — the EDIT row naming this section (one home per act: the
 *  primary is the seat's, never a card's link). */
export function sectionActOf(acts: readonly EntityAct[], section: string): EntityAct | undefined {
  return acts.find((a) => a.kind === 'edit' && a.section === section);
}

// ── Menu rows — `ActionMenu`'s input, derived from the acts ─────────────────

/** A registry action resolved for a MENU — the generic row `ActionMenu` renders
 *  (an entity's door, a list's ⋯, a note's ⋯): the label a string, the icon a
 *  node, the fence a `group` (a separator where it changes). */
export interface ResolvedAction {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Navigate (real link — modifier-click opens a new tab) or run a handler. */
  run: ActionRun;
  tone?: 'default' | 'danger';
  /** Visual group — a separator is inserted where this changes. */
  group?: string;
  /** Non-actionable (e.g. a coming-soon verb advertised but not yet wired). */
  disabled?: boolean;
  /** Why it is disabled — the row's second line, under the label. */
  note?: string;
  /** Trailing adornment (e.g. a "Soon" Badge on a coming-soon verb). */
  trailing?: ReactNode;
}

/** The fence a row sits behind — derived from its kind: the danger act is a
 *  lifecycle row in the danger tone. Separators fall where the fence changes. */
export type ActionFence = 'primary' | 'edit' | 'operation' | 'lifecycle' | 'danger';
export function fenceOf(act: Pick<EntityAct, 'kind' | 'tone'>): ActionFence {
  return act.kind === 'lifecycle' && act.tone === 'danger' ? 'danger' : act.kind;
}
const FENCE_RANK: Record<ActionFence, number> = { primary: 0, edit: 1, operation: 2, lifecycle: 3, danger: 4 };

/** Every act as a menu row, fenced by kind (primary → edit → operation →
 *  lifecycle → danger), the registry's order within a fence. A chooser row runs
 *  `openChooser` (the host mounts the `ChoiceDialog`); a primary holding outcomes
 *  is its outcomes, in its place (a menu has no menu inside). The menu glyph is
 *  drawn at 16; a disabled row says why on its second line ("4 lines undecided"). */
export function toMenuRows(acts: readonly EntityAct[], openChooser?: (act: EntityAct) => void): ResolvedAction[] {
  return acts
    .map((act, i) => ({ act, i, fence: fenceOf(act) }))
    .sort((a, b) => FENCE_RANK[a.fence] - FENCE_RANK[b.fence] || a.i - b.i)
    .flatMap(({ act, fence }) =>
      act.outcomes
        ? outcomeRows(act)
        : [
            {
              id: act.id,
              label: act.label,
              icon: act.icon ? createElement(act.icon, { size: 16, 'aria-hidden': true }) : undefined,
              run: act.run ?? { handler: () => openChooser?.(act) },
              tone: act.tone,
              group: fence,
              disabled: act.disabled,
              note: act.disabled ? act.disabledNote : undefined,
            },
          ],
    );
}

/** A primary's outcomes as menu rows — the peek's main button opens them; the
 *  danger outcome fenced last behind a separator. */
export function outcomeRows(act: Pick<EntityAct, 'outcomes'>): ResolvedAction[] {
  return [...(act.outcomes ?? [])]
    .map((o, i) => ({ o, i }))
    .sort((a, b) => Number(a.o.tone === 'danger') - Number(b.o.tone === 'danger') || a.i - b.i)
    .map(({ o }) => ({
      id: o.id,
      label: o.label,
      icon: o.icon ? createElement(o.icon, { size: 16, 'aria-hidden': true }) : undefined,
      run: o.run,
      tone: o.tone,
      group: o.tone === 'danger' ? 'danger' : 'outcome',
      disabled: o.disabled,
      note: o.disabled ? o.disabledNote : undefined,
    }));
}

/** Where a surface sits — the page's door leads with the section-less edits
 *  (a row with no card — the asset's Device settings, ADR-036 §7 exception 2);
 *  the peek holds its edits in its Edit menu (a pageless entity's sectioned ones
 *  on its cards — exception 1), so its door never carries one. */
export type ActHost = 'page' | 'peek';

/** The Actions door — operations, then the lifecycle, then the danger act,
 *  fenced; never a sectioned edit, never the primary (the seat / the main button). */
export function doorOf(acts: readonly EntityAct[], host: ActHost, openChooser?: (act: EntityAct) => void): ResolvedAction[] {
  return toMenuRows(
    acts.filter((a) => a.kind === 'operation' || a.kind === 'lifecycle' || (host === 'page' && a.kind === 'edit' && a.section === undefined)),
    openChooser,
  );
}
